import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { errorToastFromAxios } from "../../lib/supportToast";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Wallet, ArrowDownToLine, ArrowUpFromLine, Building2, Lock, RefreshCw,
  CheckCircle2, BarChart3, CreditCard, Plus, TrendingUp, Trash2, Activity,
} from "lucide-react";
import StepUpModal from "../../components/StepUpModal";
import WalletActivityPanel from "../../components/WalletActivityPanel";
import BankInstructionsCard from "../../components/BankInstructionsCard";
import SellerCreditPanel from "./SellerCreditPanel";
import PaymentsTabBar from "../../components/PaymentsTabBar";
import VerqoAccountCard from "../../components/VerqoAccountCard";
import LimitRequestCard from "../../components/LimitRequestCard";
import PlaidLinkButton from "../../components/PlaidLinkButton";

const VALID_VIEWS = ["overview", "wallet", "credit", "methods", "activity"];
const VIEW_LABEL = {
  overview: "Payouts overview",
  wallet:   "Wallet",
  credit:   "Credit & financing",
  methods:  "Funding methods",
  activity: "Activity & statements",
};

// ─────────────────────────────────────────────────────────────────────────────
// SellerWallet — Payouts hub with 3 hash-routed sub-views.
//   #overview → high-level KPIs, payout cadence, lifetime stats
//   #wallet   → full balance card + withdraw + transactions (the original page)
//   #methods  → linked banks + add/remove
// ─────────────────────────────────────────────────────────────────────────────
export default function SellerWallet() {
  const nav = useNavigate();
  const loc = useLocation();
  const view = useMemo(() => {
    const h = (loc.hash || "").replace("#", "");
    return VALID_VIEWS.includes(h) ? h : "wallet";
  }, [loc.hash]);

  const [wallet, setWallet] = useState(null);
  const [methods, setMethods] = useState([]);
  const [billing, setBilling] = useState(null);   // current SaaS plan (drives RTP %)
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [bankId, setBankId] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("ach");   // 'ach' | 'instant'
  const [withdrawStepUp, setWithdrawStepUp] = useState(false);

  // ── Add-funds (top-up) state ─────────────────────────────────────────
  // Sellers need to top up their wallet to pay SaaS subscription, ACH
  // fees, instant-payout fees, etc. Backend already supports it via
  // POST /wallet/topup — we just expose the UI on the seller side too
  // (was buyer-only before Feb 2026 — bug that prevented sellers from
  // pre-loading their wallet to cover automated billing).
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState(500);
  // Default to standard ACH ($0.50 flat) — cheapest funding rail.
  // Sellers can switch to same-day or wire for faster posting.
  const [topupSource, setTopupSource] = useState("ach");
  const [topupStepUp, setTopupStepUp] = useState(false);

  const load = () => Promise.all([
    api.get("/wallet"), api.get("/payment-methods"),
    api.get("/billing/status").catch(() => ({ data: null })),
  ]).then(([w, m, b]) => { setWallet(w.data); setMethods(m.data); setBilling(b.data || null); });
  useEffect(() => { load(); }, []);

  // One bank per business, so it is always the destination — no picking.
  useEffect(() => {
    const first = methods.find((m) => m.type === "bank");
    if (first && !bankId) setBankId(first.id);
  }, [methods, bankId]);

  // One bank per business. Legacy accounts may still carry older entries, so
  // the active one wins and the rest are offered for removal in Methods.
  const allBanks = methods.filter(m => m.type === "bank");
  const activeBank = allBanks.find(b => b.is_default) || allBanks[allBanks.length - 1] || null;
  const banks = activeBank ? [activeBank] : [];
  const staleBanks = allBanks.filter(b => b.id !== activeBank?.id);
  const txns = wallet?.transactions || [];

  // Feb 2026 — Instant payout (RTP) tiered by SaaS plan.
  //   Starter / Growth   → 0.50%
  //   Pro / Enterprise   → 0.30%
  const planId = (billing?.plan_id || billing?.plan || "starter").toLowerCase();
  const instantPct = ["pro", "enterprise"].includes(planId) ? 0.30 : 0.50;
  const amountNum = Number(amount) || 0;
  // Fees must match what the backend charges: $0.50 standard ACH, $1.00
  // same-day ACH, and a percentage on an instant payout.
  const withdrawFee = payoutMethod === "instant"
    ? Math.round(amountNum * instantPct) / 100
    : amountNum > 0 ? (payoutMethod === "ach_sameday" ? 1.00 : 0.50)
    : 0;
  const totalDebit = amountNum + withdrawFee;

  // One bank per business: a new connection replaces whatever was there.
  const replaceBank = async () => {
    try {
      const fresh = (await api.get("/payment-methods")).data || [];
      const freshBanks = fresh.filter((m) => m.type === "bank");
      const keep = freshBanks[freshBanks.length - 1];
      await Promise.all(
        freshBanks.filter((b) => b.id !== keep?.id)
          .map((b) => api.delete(`/payment-methods/${b.id}`).catch(() => null)),
      );
      toast.success(`${keep?.bank_name || "Bank"} connected. Withdrawals go here.`);
      setBankId(keep?.id || "");
      load();
    } catch (e) {
      errorToastFromAxios(e, "The bank connected, but we could not refresh the list.");
    }
  };

  const doWithdraw = async (stepUpToken) => {
    const a = Number(amount);
    if (!a || a <= 0) return toast.error("Enter a valid amount");
    if (totalDebit > (wallet?.available_balance ?? wallet?.balance ?? 0)) {
      return toast.error("Insufficient wallet balance (need to cover the fee too)");
    }
    if (banks.length > 0 && !bankId) return toast.error("Pick a destination bank account");
    try {
      const r = await api.post(
        "/wallet/withdraw",
        { amount: a, method: payoutMethod, method_id: bankId || null },
        stepUpToken ? { headers: { "X-Step-Up-Token": stepUpToken } } : undefined,
      );
      const settles = (r.data?.settles_in) || (payoutMethod === "instant" ? "within 30 min" : "1 business day");
      toast.success(`${money(a)} sent — settles in ${settles}`);
      setWithdrawOpen(false); setWithdrawStepUp(false); setAmount(""); setBankId(""); setPayoutMethod("ach");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.code === "stepup_required") {
        // Backend wants SMS verification — open the step-up modal.
        // Keep the withdraw dialog open underneath so user keeps context.
        setWithdrawStepUp(true);
        return;
      }
      errorToastFromAxios(e, "Withdrawal failed", {
        subject: `Wallet withdrawal failed · ${money(a)}`,
        body:
          `I tried to withdraw ${money(a)} from my verqo wallet and it failed.\n` +
          `Method: ${payoutMethod}\n` +
          `Destination bank: ${bankId ? `method id ${bankId}` : "default ACH"}\n` +
          `Wallet balance at time of attempt: ${money(wallet?.available_balance ?? wallet?.balance ?? 0)}`,
        urgency: "high",
        category: "payouts",
      });
    }
  };

  const removeMethod = async (id) => {
    try {
      await api.delete(`/payment-methods/${id}`);
      toast.success("Bank account removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  // ── Top-up (add funds) — same shape as the buyer flow, surfaced so
  // sellers can pre-load their wallet for SaaS billing + transaction fees.
  const doTopup = async (stepUpToken) => {
    const a = Number(topupAmount);
    if (!a || a <= 0) {
      toast.error("Enter an amount greater than $0");
      return;
    }
    try {
      // Bank-rail topups (ach / ach_sameday / rfp) go through the shared
      // /wallet/topup endpoint, which resolves to whichever rail BANK_RAIL
      // selects. Wyzia was removed in Jun 2026; Shield is the active rail and
      // has no RFP, so that option degrades to same-day ACH server-side.
      const url  = "/wallet/topup";
      const body = { amount: a, method_type: topupSource };
      const r = await api.post(
        url, body,
        stepUpToken ? { headers: { "X-Step-Up-Token": stepUpToken } } : undefined,
      );
      if (r.data?.page_url) {
        toast.success(`Redirecting to your bank to authorize ${money(a)}…`);
        window.location.href = r.data.page_url;
        return;
      }
      toast.success(`Added ${money(a)} to wallet via ${topupSource.toUpperCase()}`);
      setTopupOpen(false);
      setTopupAmount(500);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.code === "stepup_required") {
        setTopupStepUp(true);
        return;
      }
      errorToastFromAxios(e, "Top-up failed", {
        subject: `Wallet top-up failed · ${money(a)} via ${topupSource.toUpperCase()}`,
        body:
          `I tried to add ${money(a)} to my verqo wallet via ${topupSource.toUpperCase()} ` +
          `and got an error.`,
        urgency: "high",
        category: "billing",
      });
    }
  };

  if (!wallet) return <div className="p-12 text-foreground/40">Loading…</div>;

  // ── KPIs ───────────────────────────────────────────────────────────────
  const lifetimeIn  = txns.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const lifetimeOut = Math.abs(txns.filter(t => t.type === "withdraw").reduce((s, t) => s + t.amount, 0));
  const last30 = txns.filter(t => {
    if (!t.created_at) return false;
    return (Date.now() - new Date(t.created_at).getTime()) < 30 * 86400000;
  });
  const last30In  = last30.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const last30Out = Math.abs(last30.filter(t => t.type === "withdraw").reduce((s, t) => s + t.amount, 0));

  return (
    <div data-testid="seller-payouts">
      <PageHeader
        title="Payouts"
        subtitle={VIEW_LABEL[view]}
      />

      {/* Sticky tab strip — shared with SellerInvoices so the strip
          persists when the seller flips to the Invoices route. */}
      <PaymentsTabBar role="seller"/>

      <div className="p-8 max-w-5xl">
        {view === "overview" && (
          <div className="space-y-6" data-testid="payouts-overview">
            <div className="grid md:grid-cols-4 gap-3">
              <Stat label="Available now" value={money(wallet.available_balance ?? wallet.balance)} accent/>
              <Stat label="Lifetime earnings" value={money(lifetimeIn)}/>
              <Stat label="Withdrawn (30d)" value={money(last30Out)}/>
              <Stat label="Earnings (30d)" value={money(last30In)}/>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white border border-border p-6">
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Payout cadence</div>
                <div className="display text-xl mt-2 tracking-tight">D+1 to bank</div>
                <p className="text-sm text-foreground/60 mt-2 leading-relaxed">
                  Earnings from invoice payments and claims land here automatically. Withdraw to your bank anytime — standard ACH ($0.50, next day) or Instant payout (funds within 30 min).
                </p>
                <Button onClick={() => nav("/seller/wallet#wallet")} variant="outline"
                  className="mt-4 rounded-full" data-testid="overview-go-wallet">
                  Open wallet <ArrowDownToLine className="w-3.5 h-3.5 ml-1.5"/>
                </Button>
              </div>
              <div className="rounded-2xl bg-white border border-border p-6">
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Funding methods</div>
                <div className="display text-xl mt-2 tracking-tight">{banks.length} bank{banks.length !== 1 ? "s" : ""} linked</div>
                <p className="text-sm text-foreground/60 mt-2 leading-relaxed">
                  Add up to 5 destination bank accounts. ACH withdrawals go to your default account by default.
                </p>
                <Button onClick={() => nav("/seller/wallet#methods")} variant="outline"
                  className="mt-4 rounded-full" data-testid="overview-go-methods">
                  Manage methods <CreditCard className="w-3.5 h-3.5 ml-1.5"/>
                </Button>
              </div>
            </div>

            {/* Mini activity peek */}
            <div className="rounded-2xl bg-white border border-border">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Latest</div>
                  <h3 className="display text-lg mt-1">Recent payout activity</h3>
                </div>
                <button onClick={() => nav("/seller/wallet#wallet")}
                  className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center" data-testid="overview-view-all">
                  View all <TrendingUp className="w-3 h-3 ml-1"/>
                </button>
              </div>
              {txns.length === 0 ? (
                <div className="p-10 text-center text-sm text-foreground/55">No transactions yet</div>
              ) : (
                <div className="divide-y divide-border">
                  {txns.slice(0, 4).map((tx, i) => <TxnRow key={i} tx={tx} idx={i} />)}
                </div>
              )}
            </div>
          </div>
        )}

        {view === "wallet" && (
          <div className="space-y-6" data-testid="payouts-wallet">
            {/* Hero balance card (the original wallet content) */}
            <div className="rounded-3xl bg-gradient-to-br from-foreground to-foreground/85 text-background p-9">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] mono uppercase tracking-[0.2em] text-background/55">Available balance</div>
                  <div className="display text-6xl mt-2 tracking-tight">{money(wallet.available_balance ?? wallet.balance)}</div>
                  <div className="text-xs text-background/55 mt-2 flex items-center gap-2 flex-wrap">
                    <span>{wallet.currency} · last updated just now</span>
                    {wallet.mt_configured && (
                      <span className={`chip text-[9px] mono uppercase tracking-widest border ${wallet.mt_in_sync ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200" : "bg-amber-500/15 border-amber-400/40 text-amber-200"}`}>
                        {wallet.mt_in_sync ? "ledger in sync" : "ledger syncing"}
                      </span>
                    )}
                  </div>
                  {wallet.pending_balance > 0 && wallet.pending_balance !== wallet.available_balance && (
                    <div className="text-[11px] text-background/50 mt-2">
                      Pending {money(wallet.pending_balance)} · clears 1 business day
                    </div>
                  )}
                </div>
                <Wallet className="w-10 h-10 text-background/30" strokeWidth={1.5}/>
              </div>

              <div className="mt-8 flex gap-2 flex-wrap">
                <Button onClick={() => setTopupOpen(true)} data-testid="seller-wallet-topup"
                  className="rounded-full bg-background text-foreground hover:bg-background/90 h-11 px-5">
                  <Plus className="w-4 h-4 mr-1.5"/>Add funds
                </Button>
                <Button onClick={() => setWithdrawOpen(true)} data-testid="seller-wallet-withdraw"
                  variant="outline"
                  className="rounded-full border-background/30 text-background hover:bg-background/10 h-11 px-5">
                  <ArrowDownToLine className="w-4 h-4 mr-1.5"/>Withdraw to bank
                </Button>
              </div>
              <p className="mt-3 text-[11px] text-background/50">
                Add funds to cover your monthly SaaS subscription, ACH fees, instant-payout
                fees, and any platform billing. Earnings from invoice payments and claims
                also land here automatically — withdraw anytime via $0.50 ACH.
              </p>
            </div>

            {/* The seller's own account number — buyers and marketplaces pay into it */}
            <VerqoAccountCard onChange={load} />

            <LimitRequestCard />

            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-4">
              <Stat label="Lifetime earnings" value={money(lifetimeIn)}/>
              <Stat label="Withdrawn" value={money(lifetimeOut)}/>
              <Stat label="Connected bank" value={banks.length ? (banks[0].bank_name || "Linked") : "None"}/>
            </div>

            {/* Recent transactions */}
            <div className="rounded-2xl bg-white border border-border">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Activity</div>
                  <h3 className="display text-lg mt-1">Recent transactions</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={load} className="h-9 rounded-full">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh
                </Button>
              </div>
              {txns.length === 0 ? (
                <div className="p-10 text-center text-sm text-foreground/55">No transactions yet</div>
              ) : (
                <div className="divide-y divide-border">
                  {txns.slice(0, 12).map((tx, i) => <TxnRow key={i} tx={tx} idx={i} />)}
                </div>
              )}
            </div>
          </div>
        )}

        {view === "methods" && (
          <div className="space-y-6" data-testid="payouts-methods">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="display text-2xl tracking-tight">Your bank</h2>
                <p className="text-sm text-foreground/55 mt-1">
                  One connected bank, used for every withdrawal. Connecting a new one replaces it.
                </p>
              </div>
              {banks.length > 0 && (
                <PlaidLinkButton label="Replace bank" variant="secondary" onConnected={replaceBank} />
              )}
            </div>

            {banks.length === 0 ? (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-10 text-center"
                   data-testid="seller-no-bank">
                <Building2 className="w-8 h-8 mx-auto text-amber-700"/>
                <div className="display text-lg mt-3 text-amber-900">No bank connected yet</div>
                <p className="text-sm text-amber-800/80 mt-1 max-w-sm mx-auto">
                  Connect your bank to withdraw your earnings. It links instantly — no
                  micro-deposits, no routing numbers to type.
                </p>
                <div className="mt-5 flex justify-center">
                  <PlaidLinkButton label="Connect your bank" onConnected={replaceBank} />
                </div>
              </div>
            ) : (
              <div className="rounded-2xl bg-white border border-border divide-y divide-border">
                {banks.map(b => (
                  <SellerBankRow key={b.id} b={b} onRemove={() => removeMethod(b.id)}/>
                ))}
              </div>
            )}

            <VerqoAccountCard compact onChange={load} />

            {staleBanks.length > 0 && (
              <div className="rounded-2xl border border-border bg-white p-6" data-testid="seller-stale-banks">
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">
                  Older bank records
                </div>
                <p className="mt-1 text-sm text-foreground/55">
                  These are no longer used for payouts. Remove them to keep things tidy.
                </p>
                <div className="mt-3 divide-y divide-border">
                  {staleBanks.map(b => (
                    <div key={b.id} className="flex items-center justify-between py-3"
                         data-testid={`stale-bank-${b.id}`}>
                      <div className="text-sm text-foreground/70">
                        {b.bank_name || b.label || "Bank account"}
                        <span className="mono ml-2 text-[11px] text-foreground/45">··· {b.last4 || "----"}</span>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeMethod(b.id)}
                              data-testid={`stale-bank-remove-${b.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl bg-white border border-border p-6">
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">How payouts work</div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/70">
                <li className="flex gap-2"><span className="text-emerald-700 mt-0.5">✓</span>Funds land in your wallet the day buyers pay invoices.</li>
                <li className="flex gap-2"><span className="text-emerald-700 mt-0.5">✓</span>Withdraw any amount, any time — no minimum.</li>
                <li className="flex gap-2"><span className="text-emerald-700 mt-0.5">✓</span>Standard ACH $0.50 (next day), same-day ACH $1.00, or instant within 30 min ({instantPct.toFixed(2)}% on your plan).</li>
              </ul>
            </div>
          </div>
        )}

        {view === "activity" && (
          <div data-testid="payouts-activity">
            <WalletActivityPanel
              title="All your earnings & withdrawals."
              subtitle="Search, filter, and download a signed monthly statement for any month with payout activity."
              testIdPrefix="seller-activity"
              role="seller"
            />
          </div>
        )}

        {view === "credit" && (
          <div data-testid="payouts-credit">
            <SellerCreditPanel/>
          </div>
        )}
      </div>

      {/* Withdraw dialog (shared across views) */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="max-w-md" data-testid="seller-withdraw-dialog">
          <DialogHeader><DialogTitle className="display text-2xl tracking-tight">Withdraw to your bank</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground/55 -mt-1">Pick a payout rail. Fees match the published Payments Pricing page.</p>

          <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50 mt-3">Amount</Label>
          <div className="relative mt-1.5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40">$</span>
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="1000" className="h-12 pl-7 text-lg display" data-testid="seller-withdraw-amount"/>
          </div>
          <button onClick={() => setAmount(wallet.available_balance ?? wallet.balance)} className="text-[11px] mt-1 text-foreground/55 hover:text-foreground">Withdraw full balance ({money(wallet.available_balance ?? wallet.balance)})</button>

          <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50 mt-4">Payout speed</Label>
          <div className="grid gap-2 mt-1.5">
            {[
              { v: "ach",          icon: Building2,  l: "ACH · standard (next-day)", fee: "$0.50",                          hint: "Settles in 1 business day" },
              { v: "ach_sameday",  icon: Building2,  l: "ACH · same-day",            fee: "$1.00",                          hint: "Settles same business day · cutoff ~3 pm ET" },
              { v: "instant",      icon: TrendingUp, l: "Instant payout",            fee: `${instantPct.toFixed(2)}%`,      hint: `Funds within 30 min · ${["pro","enterprise"].includes(planId) ? "Pro/Enterprise rate" : "Starter/Growth rate"}` },
            ].map(opt => {
              const Icon = opt.icon;
              const sel = payoutMethod === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setPayoutMethod(opt.v)}
                  data-testid={`seller-withdraw-method-${opt.v}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${sel ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}
                >
                  <Icon className="w-4 h-4 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium flex items-center justify-between gap-2">
                      <span className="truncate">{opt.l}</span>
                      <span className="mono text-[12.5px] text-foreground/70">{opt.fee}</span>
                    </div>
                    <div className="text-[11px] text-foreground/55">{opt.hint}</div>
                  </div>
                  {sel && <CheckCircle2 className="w-4 h-4 text-foreground shrink-0"/>}
                </button>
              );
            })}
          </div>

          {amountNum > 0 && (
            <div className="mt-3 rounded-xl bg-foreground/[0.03] border border-border p-3 text-[12.5px] space-y-1" data-testid="seller-withdraw-fee-preview">
              <div className="flex justify-between"><span className="text-foreground/55">Amount to bank</span><span className="mono">{money(amountNum)}</span></div>
              <div className="flex justify-between"><span className="text-foreground/55">{payoutMethod === "instant" ? `Instant fee (${instantPct.toFixed(2)}%)` : payoutMethod === "ach_sameday" ? "Same-day ACH fee" : "ACH fee (flat)"}</span><span className="mono">{money(withdrawFee)}</span></div>
              <div className="flex justify-between border-t border-border pt-1 mt-1 font-medium"><span>Wallet deduction</span><span className="mono">{money(totalDebit)}</span></div>
            </div>
          )}

          <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50 mt-4">Destination bank</Label>
          {banks.length === 0 ? (
            <div className="mt-1.5 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              No bank connected. Connect one in <strong>Methods</strong> first.
            </div>
          ) : (
            <div className="mt-1.5 space-y-1.5">
              {banks.map(b => (
                <button key={b.id} onClick={() => setBankId(b.id)} data-testid={`bank-${b.id}`}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors ${bankId === b.id ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}>
                  <Building2 className="w-4 h-4"/>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{b.bank_name || b.label || "Bank account"}</div>
                    <div className="text-[11px] text-foreground/55 mono">··· {b.last4 || "----"}</div>
                  </div>
                  {bankId === b.id && <CheckCircle2 className="w-4 h-4 text-foreground"/>}
                </button>
              ))}
            </div>
          )}

          <Button onClick={() => doWithdraw()} disabled={banks.length === 0 || !amountNum}
            data-testid="seller-withdraw-confirm"
            className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40">
            {payoutMethod === "instant" ? "Send instantly" : "Withdraw"}{amount ? ` ${money(Number(amount))}` : ""}
          </Button>
          <div className="mt-2 text-center text-[10px] text-foreground/45 inline-flex items-center justify-center gap-2 w-full">
            <Lock className="w-2.5 h-2.5"/>SSL · Bank-grade security
          </div>
        </DialogContent>
      </Dialog>

      {/* ───── SMS step-up confirmation for withdraw to bank ───── */}
      <StepUpModal
        open={withdrawStepUp}
        onClose={() => setWithdrawStepUp(false)}
        action="wallet_withdraw"
        amount={Number(amount)}
        title={`Confirm ${amount ? money(Number(amount)) : ""} payout`}
        description="For your security, every withdrawal to bank requires a one-time SMS code."
        onVerified={(token) => doWithdraw(token)}
      />

      {/* ───── Add funds dialog (Feb 2026 — sellers can now pre-load wallet) ───── */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent className="max-w-md" data-testid="seller-topup-dialog">
          <DialogHeader>
            <DialogTitle>Add funds to your wallet</DialogTitle>
          </DialogHeader>
          <p className="text-[12.5px] text-foreground/65 leading-snug mb-4">
            Used to cover your monthly SaaS subscription and per-transaction
            fees (ACH, wire, instant payouts). Pick the funding rail that
            matches how fast you need the money to post.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-[12px]">Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/45 text-sm">$</span>
                <Input
                  type="number"
                  step="50"
                  min="50"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  className="pl-7 mono text-base h-11"
                  data-testid="seller-topup-amount"
                  autoFocus
                />
              </div>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {[100, 500, 1000, 2500, 5000].map(a => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setTopupAmount(a)}
                    data-testid={`seller-topup-quick-${a}`}
                    className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${Number(topupAmount) === a ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:bg-secondary/40"}`}
                  >
                    ${a.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[12px]">Funding method</Label>
              <div className="grid gap-2 mt-1.5">
                {[
                  { v: "ach",         icon: Building2, l: "ACH · standard (next-day)", fee: "$0.50",  hint: "Cheapest · posts in 1 business day" },
                  { v: "ach_sameday", icon: Activity,  l: "ACH · same-day",            fee: "$3.50",  hint: "Posts within hours" },
                  { v: "rfp",         icon: TrendingUp, l: "Instant Payment Request",  fee: "$5.00",  hint: "Funds within 30 min" },
                  { v: "wire",        icon: Building2, l: "Wire · domestic",           fee: "$15.00", hint: "Same-day for very large transfers" },
                  { v: "card",        icon: CreditCard, l: "Credit / debit card",      fee: "4% (3% Growth)", hint: "Instant · highest cost" },
                ].map(opt => {
                  const Icon = opt.icon;
                  const active = topupSource === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setTopupSource(opt.v)}
                      data-testid={`seller-topup-method-${opt.v}`}
                      className={`flex items-center gap-3 rounded-lg px-3.5 py-2.5 border text-left transition-colors ${active ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:bg-secondary/40"}`}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${active ? "" : "text-foreground/55"}`}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium">{opt.l}</div>
                        <div className={`text-[10.5px] mt-0.5 ${active ? "text-background/65" : "text-foreground/50"}`}>{opt.hint}</div>
                      </div>
                      <div className={`text-[11px] mono font-semibold ${active ? "text-background" : "text-foreground/75"}`}>{opt.fee}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={() => setTopupOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={() => doTopup()} data-testid="seller-topup-submit"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90">
              <Plus className="w-4 h-4 mr-1.5"/>
              Add {topupAmount ? money(Number(topupAmount)) : ""}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <StepUpModal
        open={topupStepUp}
        onClose={() => setTopupStepUp(false)}
        action="wallet_topup"
        amount={Number(topupAmount)}
        title={`Confirm ${topupAmount ? money(Number(topupAmount)) : ""} top-up`}
        description="For your security, large wallet top-ups require a one-time SMS code."
        onVerified={(token) => doTopup(token)}
      />
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}>
      <div className={`text-[10px] mono uppercase tracking-[0.2em] ${accent ? "text-accent/80" : "text-foreground/50"}`}>{label}</div>
      <div className="display text-2xl mt-1.5">{value}</div>
    </div>
  );
}

function TxnRow({ tx, idx }) {
  return (
    <div className="px-6 py-3.5 flex items-center justify-between" data-testid={`tx-${idx}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${
          tx.amount > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
        }`}>
          {tx.amount > 0 ? <ArrowDownToLine className="w-4 h-4"/> : <ArrowUpFromLine className="w-4 h-4"/>}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium capitalize truncate">{(tx.type || "txn").replace(/_/g, " ")}</div>
          <div className="text-[11px] text-foreground/50 mono">
            {tx.ref || "—"} · {tx.method?.toUpperCase()} · {tx.created_at ? new Date(tx.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
          </div>
        </div>
      </div>
      <div className={`display text-base shrink-0 ${tx.amount > 0 ? "text-emerald-700" : "text-foreground"}`}>
        {tx.amount > 0 ? "+" : ""}{money(tx.amount)}
      </div>
    </div>
  );
}

// ── Seller bank row with full Mercury-style funding-instructions card ────
function SellerBankRow({ b, onRemove }) {
  const [open, setOpen] = useState(false);
  const rails = (b.rails || "").split(",").map(s => s.trim()).filter(Boolean);
  const hasInstructions = rails.length > 0 || b.iban || b.swift_bic;

  return (
    <div className="px-6 py-4" data-testid={`method-${b.id}`}>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-secondary grid place-items-center shrink-0">
          <Building2 className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{b.bank_name || b.label || "Bank account"}</div>
          <div className="text-[11px] text-foreground/55 mono">
            ··· {b.last4 || "----"} · {b.is_default ? "Default · " : ""}ACH 1-day
          </div>
        </div>
        {hasInstructions && (
          <Button
            size="sm" variant="outline"
            onClick={() => setOpen(o => !o)}
            data-testid={`method-toggle-${b.id}`}
            className="rounded-full"
          >
            {open ? "Hide instructions" : "Wire & ACH details"}
          </Button>
        )}
        {b.is_default && <span className="chip bg-accent/30 border-accent/40 text-[10px]">Default</span>}
        <button onClick={onRemove}
          data-testid={`remove-${b.id}`}
          className="text-foreground/40 hover:text-rose-700 transition-colors p-2">
          <Trash2 className="w-4 h-4"/>
        </button>
      </div>
      {open && hasInstructions && (
        <div className="mt-4 ml-14 pt-4 border-t border-border" data-testid={`method-reveal-${b.id}`}>
          <BankInstructionsCard method={b} testIdRoot={`method-${b.id}-instr`}/>
        </div>
      )}
    </div>
  );
}
